/* =========================================================================
 * Kkumi · Cart line editor panel (cart v14)
 * -------------------------------------------------------------------------
 * "Edit" on a cart row (`[data-kkc-edit-open="<line key>"]`) opens ONE panel
 * — a right-hand drawer from 640px, full screen below — for both kinds of
 * line, fed by the JSON snippets/kkumi-cart-variant-edit.liquid renders:
 *
 *   variant  each product option as chips; a chip whose combination (with the
 *            other current picks) is sold out or doesn't exist is labelled;
 *            price per option shows when it varies; the price in the footer
 *            follows the picks live.
 *   bundle   each Simple Bundles option as chips (values from the app's
 *            metafield; a value the app lists as out of stock is
 *            "Unavailable"). Single-value options have nothing to choose and
 *            ride along unchanged.
 *
 * Nothing changes until Save. Save is disabled while nothing changed or a
 * chosen option is unavailable. Cancel / Esc / the backdrop close without
 * saving and return focus to the Edit button; a failed save keeps the panel
 * open with the original line untouched and an error message.
 *
 * Saving
 *   variant → Shopify can't change a line's variant in place, so it's a swap:
 *     ATOMIC (no properties, new variant not already in the cart): ONE
 *       /cart/update.js zeroing the old line and setting the new variant;
 *     TWO-STEP otherwise: /cart/add.js then /cart/change.js removing the old
 *       line, with a sessionStorage journal so a reload between the two never
 *       strands both lines (recover() finishes the removal on the next load).
 *   bundle → /cart/change.js with the line's new properties (each option +
 *     `_bundle_selection`, built with the app's separator rule — the same as
 *     assets/kkumi-sb-options.js). Variant and price don't change.
 * Both ask for the sections Dawn's quantity change re-renders, paint them and
 * publish `cartUpdate`, then focus the row's Edit button again.
 *
 * The two-step add carries `X-Kkumi-Silent` so the sitewide "Added to cart"
 * popup (section-cart-toast.js) stays quiet — this is an edit, not an add.
 * Copy comes from data-* on <cart-items data-kkc-items> (section settings).
 * ========================================================================= */
(function () {
  'use strict';
  if (window.__kkCartVarInit) return;
  window.__kkCartVarInit = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var esc = function (v) { return window.KKEsc(v); };
  var money = function (c) { return window.KKMoney.fromCents(c); };

  function parse(json, fallback) {
    try { return JSON.parse(json); } catch (e) { return fallback; }
  }
  function copy(key, fallback) {
    var h = document.querySelector('[data-kkc-items]');
    return (h && h.dataset[key]) || fallback;
  }
  function fill(tpl, map) {
    var out = String(tpl || '');
    Object.keys(map).forEach(function (k) { out = out.split('[' + k + ']').join(map[k]); });
    return out;
  }

  // --- Cart requests -------------------------------------------------------
  function postJSON(path, body, extraHeaders) {
    var headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (extraHeaders) Object.keys(extraHeaders).forEach(function (k) { headers[k] = extraHeaders[k]; });
    return fetch(root + path, { method: 'POST', headers: headers, body: JSON.stringify(body) })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) {
          if (!r.ok || data.status) {
            var e = new Error('cart_error');
            e.shopify = data.description || data.message || '';
            throw e;
          }
          return data;
        });
      });
  }
  function cartItemsEl() {
    var c = document.querySelector('cart-items');
    return c && typeof c.getSectionsToRender === 'function' ? c : null;
  }
  function withSections(body) {
    var c = cartItemsEl();
    if (c) {
      body.sections = c.getSectionsToRender().map(function (s) { return s.section; });
      body.sections_url = window.location.pathname;
    }
    return body;
  }
  function paint(state, variantId) {
    var c = cartItemsEl();
    if (!c) { window.location.reload(); return; }
    c.getSectionsToRender().forEach(function (section) {
      var el = document.getElementById(section.id);
      if (!el || !state.sections || !state.sections[section.section]) return;
      var target = el.querySelector(section.selector) || el;
      target.innerHTML = c.getSectionInnerHTML(state.sections[section.section], section.selector);
    });
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state, variantId: variantId });
    }
  }

  // --- Swap journal (two-step variant swaps only) --------------------------
  var JOURNAL = 'kkCartVarSwap';
  var JOURNAL_TTL = 15 * 60 * 1000;
  function store() { try { return window.sessionStorage; } catch (e) { return null; } }
  function writeJournal(j) { var st = store(); if (st) try { st.setItem(JOURNAL, JSON.stringify(j)); } catch (e) {} }
  function readJournal() { var st = store(); if (!st) return null; try { return JSON.parse(st.getItem(JOURNAL) || 'null'); } catch (e) { return null; } }
  function clearJournal() { var st = store(); if (st) try { st.removeItem(JOURNAL); } catch (e) {} }

  function variantQtyInCart(id) {
    var el = document.querySelector('#main-cart-items [data-quantity-variant-id="' + id + '"]');
    return el ? Number(el.getAttribute('data-cart-quantity')) || 0 : 0;
  }

  // A load that finds a journal: if the old line is still there AND the new
  // variant's count shows the add went through, finish the swap. Cleared
  // BEFORE the removal so a failure can never become a reload loop.
  function recover() {
    var j = readJournal();
    if (!j) return;
    if (!j.oldKey || !j.newId || !j.t || Date.now() - j.t > JOURNAL_TTL) { clearJournal(); return; }
    fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var oldLine = null;
        var newTotal = 0;
        (cart.items || []).forEach(function (it) {
          if (it.key === j.oldKey) oldLine = it;
          if (String(it.variant_id) === String(j.newId)) newTotal += it.quantity;
        });
        clearJournal();
        if (!oldLine || newTotal < (j.before || 0) + (j.qty || 1)) return;
        return postJSON('cart/change.js', { id: j.oldKey, quantity: 0 })
          .then(function () { window.location.reload(); });
      })
      .catch(function () {});
  }
  recover();

  // --- Editor state ----------------------------------------------------------
  var wrap = null;   // panel host appended to <body>
  var E = null;      // current edit

  function splitList(raw) {
    return String(raw == null ? '' : raw).split(',').map(function (v) { return v.trim(); });
  }

  function load(key) {
    var nodes = document.querySelectorAll('[data-kkc-edit-data]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-kkc-edit-data') === key) return parse(nodes[i].textContent, null);
    }
    return null;
  }

  function prepareBundle(d) {
    var props = d.properties || {};
    d.groups = (d.options || []).map(function (o) {
      var raw = splitList(o.values);
      var inv = o.inventories ? splitList(o.inventories) : [];
      var useInv = inv.length === raw.length && String(o.inventories).trim() !== '';
      var values = [];
      raw.forEach(function (v, i) {
        if (!v) return;
        var out = useInv && !(Number(inv[i]) > 0);
        values.push({ v: v, out: out });
      });
      var cur = props[o.name] != null ? String(props[o.name]).trim() : '';
      return { name: o.name, label: o.label || o.name, values: values, current: cur };
    });
    // Separator after each option — the app's own rule (io.min.js).
    d.seps = [];
    (Array.isArray(d.v2) ? d.v2 : []).forEach(function (group) {
      if (!Array.isArray(group)) return;
      group.forEach(function (_o, n) { d.seps.push(group.length > 1 && n !== group.length - 1 ? ' ++ ' : ' <> '); });
    });
    d.picks = d.groups.map(function (g) { return g.current; });
    d.orig = JSON.stringify(d.picks);
  }

  function prepareVariant(d) {
    var cur = null;
    (d.variants || []).forEach(function (v) { if (String(v.id) === String(d.variantId)) cur = v; });
    d.picks = cur ? cur.options.slice() : (d.options || []).map(function (o) { return o.values[0]; });
    d.orig = JSON.stringify(d.picks);
    d.origPrice = cur ? cur.price : 0;
  }

  function findVariant(d, picks) {
    var list = d.variants || [];
    for (var i = 0; i < list.length; i++) {
      var ok = true;
      for (var n = 0; n < picks.length; n++) { if (list[i].options[n] !== picks[n]) { ok = false; break; } }
      if (ok) return list[i];
    }
    return null;
  }

  // --- Rendering ------------------------------------------------------------
  // The panel is built once (draw) when it opens; a pick only patches what it
  // changes (refresh): each chip's sold-out state / note / photo and the
  // footer. Rebuilding the whole panel on every pick reloaded every thumbnail
  // and flickered the drawer.

  // "Aurora (Holographic)" → title "Aurora", second line "Holographic" — the
  // same split the PDP's option cards use, so long names never run past the
  // chip.
  function splitName(v) {
    var m = /^(.*?)\s*\(([^)]*)\)\s*$/.exec(String(v));
    return m && m[1] ? { title: m[1], sub: m[2] } : { title: String(v), sub: '' };
  }

  function chipText(value, note) {
    var nm = splitName(value);
    return '<span class="kkc-opt__n">' + esc(nm.title) + '</span>' +
      (nm.sub ? '<small class="kkc-opt__sub">' + esc(nm.sub) + '</small>' : '') +
      (note ? '<small class="kkc-opt__note">' + esc(note) + '</small>' : '');
  }

  function chip(n, j, value, st, checked) {
    return '<label class="kkc-opt' + (st.out ? ' kkc-opt--off' : '') + (st.img ? ' kkc-opt--thumb' : '') + '" data-j="' + j + '">' +
      '<input type="radio" name="kkc-ed-' + n + '" value="' + esc(value) + '"' + (checked ? ' checked' : '') + (st.disabled || E.saving ? ' disabled' : '') + ' data-fid="ed|' + n + '|' + j + '">' +
      (st.img ? '<img class="kkc-opt__img" src="' + esc(st.img) + '" alt="" width="40" height="40">' : '') +
      '<span class="kkc-opt__t">' + chipText(value, st.note) + '</span>' +
    '</label>';
  }

  // One option group's chip states: { label, values[], thumbs, states[] }.
  function groupState(n) {
    var d = E.data;
    if (d.kind === 'bundle') {
      var g = d.groups[n];
      var map = d.thumbs && d.thumbs[g.name] ? d.thumbs[g.name] : null;
      var states = g.values.map(function (o) {
        var checked = d.picks[n] === o.v;
        return { out: o.out, note: o.out ? copy('edUnavailable', 'Unavailable') : '', img: map ? map[o.v] || null : null, disabled: o.out && !checked };
      });
      return { label: g.label, values: g.values.map(function (o) { return o.v; }), thumbs: !!map, states: states, hidden: g.values.length < 2 };
    }
    var opt = d.options[n];
    var cands = opt.values.map(function (val) {
      var t = d.picks.slice(); t[n] = val;
      return findVariant(d, t);
    });
    var prices = cands.map(function (v) { return v ? v.price : null; });
    var vary = prices.filter(function (p, i, a) { return p != null && a.indexOf(p) === i; }).length > 1;
    // Photos: the image of the variant each chip would pick. Shown only when
    // every value has one and they differ (same rule as the PDP's cards).
    var imgs = opt.values.map(function (val, j) {
      var v2 = cands[j] || (d.variants || []).filter(function (x) { return x.options[n] === val; })[0];
      return v2 && v2.image ? v2.image : null;
    });
    var distinct = imgs.filter(function (u, i, a) { return u && a.indexOf(u) === i; }).length;
    var thumbs = imgs.every(Boolean) && distinct === imgs.length && imgs.length > 1;
    var states = cands.map(function (v, j) {
      var out = !v || !v.available;
      return { out: out, note: out ? copy('edSoldout', 'Sold out') : (vary && prices[j] != null ? money(prices[j]) : ''), img: thumbs ? imgs[j] : null, disabled: false };
    });
    return { label: opt.name, values: opt.values, thumbs: thumbs, states: states, hidden: false };
  }

  function groupCount() { return E.data.kind === 'bundle' ? E.data.groups.length : (E.data.options || []).length; }

  function problemsList() {
    var d = E.data;
    var out = [];
    if (d.kind === 'bundle') {
      d.groups.forEach(function (g, n) {
        var o = g.values.filter(function (x) { return x.v === d.picks[n]; })[0];
        if (o && o.out) out.push(fill(copy('edProblem', '[value] isn’t available. Choose another [option].'), { value: o.v, option: String(g.label).toLowerCase() }));
      });
      return out;
    }
    // One message for the picked combination, naming the option changed last.
    var cur = findVariant(d, d.picks);
    if (!cur || !cur.available) {
      var last = E.lastOption != null ? E.lastOption : 0;
      var optName = d.options[last] ? String(d.options[last].name).toLowerCase() : '';
      out.push(fill(copy('edProblem', '[value] isn’t available. Choose another [option].'), { value: d.picks.join(' · '), option: optName }));
    }
    return out;
  }

  function bodyHTML() {
    var d = E.data;
    var html = '';
    if (d.kind === 'bundle' && d.quantity > 1) html += '<p class="kkc-grp__h">' + esc(fill(copy('edBundleQty', 'Your choices apply to each of your [qty] bundles.'), { qty: d.quantity })) + '</p>';
    for (var n = 0; n < groupCount(); n++) {
      var gs = groupState(n);
      if (gs.hidden) continue; // nothing to choose; it rides along
      html += '<fieldset class="kkc-grp" data-n="' + n + '"><legend>' + esc(gs.label) + '</legend><div class="kkc-ch' + (gs.thumbs ? ' kkc-ch--thumbs' : '') + '">' +
        gs.values.map(function (val, j) { return chip(n, j, val, gs.states[j], d.picks[n] === val); }).join('') +
      '</div></fieldset>';
    }
    return html;
  }

  function currentPrice() {
    var d = E.data;
    if (d.kind === 'bundle') return d.price;
    var v = findVariant(d, d.picks);
    return v ? v.price : d.origPrice;
  }

  function footHTML() {
    var d = E.data;
    var isBundle = d.kind === 'bundle';
    var problems = problemsList();
    var dirty = JSON.stringify(d.picks) !== d.orig;
    var blocked = E.saving || problems.length > 0 || !dirty;
    var price = currentPrice();
    var origPrice = isBundle ? d.price : d.origPrice;
    return problems.map(function (p) { return '<p class="kkc-err" role="alert">' + esc(p) + '</p>'; }).join('') +
      (E.err ? '<p class="kkc-err" role="alert">' + esc(E.err) + '</p>' : '') +
      '<div class="kkc-dr__price" aria-live="polite"><span>' + esc(isBundle ? copy('edBundlePrice', 'Bundle price') : copy('edPrice', 'Price')) +
        (d.quantity > 1 ? ' ' + esc(copy('edEach', '(each)')) : '') + '</span><span>' +
        (price !== origPrice ? '<s>' + esc(money(origPrice)) + '</s>' : '') + '<b>' + esc(money(price)) + '</b></span></div>' +
      '<div class="kkc-dr__btns">' +
        '<button type="button" class="button button--secondary" data-ed="cancel" data-fid="cancel"' + (E.saving ? ' aria-disabled="true"' : '') + '>' + esc(copy('edCancel', 'Cancel')) + '</button>' +
        '<button type="button" class="button" data-ed="save" data-fid="save"' + (blocked ? ' aria-disabled="true"' : '') + '>' + esc(copy('edSave', 'Save changes')) + '</button>' +
      '</div>';
  }

  function drawFoot() {
    var f = wrap.querySelector('.kkc-dr__f');
    if (!f) return;
    var active = document.activeElement;
    var fid = active && f.contains(active) ? active.getAttribute('data-fid') : null;
    f.innerHTML = footHTML();
    var save = f.querySelector('[data-ed="save"]');
    if (E.saving && save && window.KKBtn) window.KKBtn.busy(save, copy('edSaving', 'Saving…'));
    if (fid) { var again = f.querySelector('[data-fid="' + fid + '"]'); if (again) again.focus({ preventScroll: true }); }
  }

  // Patch the chips in place after a pick (or once bundle photos arrive).
  function refresh() {
    var needFull = false;
    wrap.querySelectorAll('.kkc-grp[data-n]').forEach(function (fs) {
      var n = Number(fs.getAttribute('data-n'));
      var gs = groupState(n);
      var grid = fs.querySelector('.kkc-ch');
      if (grid && grid.classList.contains('kkc-ch--thumbs') !== gs.thumbs) {
        if (E.data.kind === 'bundle') grid.classList.toggle('kkc-ch--thumbs', gs.thumbs);
        else { needFull = true; return; }
      }
      fs.querySelectorAll('label.kkc-opt').forEach(function (lab) {
        var j = Number(lab.getAttribute('data-j'));
        var st = gs.states[j];
        if (!st) return;
        lab.classList.toggle('kkc-opt--off', !!st.out);
        lab.classList.toggle('kkc-opt--thumb', !!st.img);
        var input = lab.querySelector('input');
        if (input) input.disabled = !!(st.disabled || E.saving);
        var img = lab.querySelector('.kkc-opt__img');
        if (st.img && !img) {
          img = document.createElement('img');
          img.className = 'kkc-opt__img'; img.alt = ''; img.width = 40; img.height = 40;
          lab.insertBefore(img, lab.querySelector('.kkc-opt__t'));
        }
        if (img && !st.img) img.remove();
        else if (img && img.getAttribute('src') !== st.img) img.setAttribute('src', st.img);
        var text = lab.querySelector('.kkc-opt__t');
        var html = chipText(gs.values[j], st.note);
        if (text && text.innerHTML !== html) text.innerHTML = html;
      });
    });
    if (needFull) { draw(); return; }
    drawFoot();
  }

  function draw() {
    var active = document.activeElement;
    var fid = active && wrap.contains(active) ? active.getAttribute('data-fid') : null;
    var scroller = wrap.querySelector('.kkc-dr__b');
    var scrollTop = scroller ? scroller.scrollTop : 0;
    var d = E.data;
    var isBundle = d.kind === 'bundle';

    wrap.innerHTML =
      '<div class="kkc-dr-wrap">' +
        '<div class="kkc-dr-back" data-ed="cancel"></div>' +
        '<div class="kkc-dr" role="dialog" aria-modal="true" aria-labelledby="KkcEdT" aria-describedby="KkcEdD">' +
          '<div class="kkc-dr__h"><div><h2 id="KkcEdT" tabindex="-1">' + esc(isBundle ? copy('edTitleBundle', 'Edit bundle') : copy('edTitleVariant', 'Edit options')) + '</h2>' +
            '<p id="KkcEdD">' + esc(fill(copy('edSub', '[name] · nothing changes until you save'), { name: d.title })) + '</p></div>' +
            '<button type="button" class="kkc-dr__x" data-ed="cancel" data-fid="x" aria-label="' + esc(copy('edClose', 'Close without saving')) + '">×</button></div>' +
          '<div class="kkc-dr__b">' + bodyHTML() + '</div>' +
          '<div class="kkc-dr__f"></div>' +
        '</div>' +
      '</div>';
    drawFoot();

    var sc = wrap.querySelector('.kkc-dr__b');
    if (sc) sc.scrollTop = scrollTop;
    if (fid) {
      var again = wrap.querySelector('[data-fid="' + (window.CSS && CSS.escape ? CSS.escape(fid) : fid) + '"]');
      if (again) again.focus({ preventScroll: true });
    }
  }

  // Bundle option photos live on the product page's option cards (resolved
  // there by snippets/kkumi-sb-options.liquid from the hero's picker
  // settings), so read them from that page once per product and reuse.
  var thumbCache = {};
  function bundleThumbs(url) {
    if (!url) return Promise.resolve({});
    if (thumbCache[url]) return thumbCache[url];
    thumbCache[url] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var out = {};
        doc.querySelectorAll('fieldset[data-kk-sb-entry][data-kk-sb-name]').forEach(function (fs) {
          var name = fs.getAttribute('data-kk-sb-name');
          var map = out[name] || (out[name] = {});
          fs.querySelectorAll('label.var-option').forEach(function (lab) {
            var input = lab.querySelector('input[data-kk-sb-input]');
            var img = lab.querySelector('.var-option__thumb img');
            if (input && img && img.getAttribute('src')) map[input.value] = img.getAttribute('src');
          });
        });
        return out;
      })
      .catch(function () { delete thumbCache[url]; return {}; });
    return thumbCache[url];
  }

  function open(key, trigger) {
    var data = load(key);
    if (!data) return;
    if (data.kind === 'bundle') prepareBundle(data); else prepareVariant(data);
    E = { data: data, key: key, line: data.line, trigger: trigger, saving: false, err: '' };
    if (data.kind === 'bundle') {
      var mine = E;
      bundleThumbs(data.url).then(function (thumbs) {
        if (E !== mine || !Object.keys(thumbs).length) return;
        data.thumbs = thumbs;
        refresh();
      });
    }
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'kkc kkc-editor';
      document.body.appendChild(wrap);
      bind();
    }
    draw();
    document.documentElement.classList.add('kkc-lock');
    requestAnimationFrame(function () {
      var h = document.getElementById('KkcEdT');
      if (h) h.focus();
    });
  }

  function close(saved, variantId) {
    if (!E) return;
    var line = E.line;
    var trigger = E.trigger;
    E = null;
    wrap.innerHTML = '';
    document.documentElement.classList.remove('kkc-lock');
    // After a save the row was re-rendered (a swap gives it a new key and
    // can move it), so find it by its variant, else by position; otherwise
    // return to the trigger.
    var target = null;
    if (saved) {
      var input = variantId ? document.querySelector('#main-cart-items [data-quantity-variant-id="' + variantId + '"]') : null;
      var row = input ? input.closest('.kkc-line') : document.getElementById('CartItem-' + line);
      target = row ? (row.querySelector('[data-kkc-edit-open]') || row) : null;
    }
    else if (trigger && trigger.isConnected) target = trigger;
    if (target) target.focus();
  }

  function save() {
    var d = E.data;
    E.saving = true;
    E.err = '';
    refresh();

    var request;
    var variantId = d.variantId;
    var twoStepAdded = false;
    if (d.kind === 'bundle') {
      var props = {};
      Object.keys(d.properties || {}).forEach(function (k) { props[k] = d.properties[k]; });
      var parts = [];
      d.groups.forEach(function (g, i) {
        props[g.name] = d.picks[i];
        if (i) parts.push(d.seps[i - 1] || ' <> ');
        parts.push(d.picks[i]);
      });
      props._bundle_selection = parts.join('');
      request = postJSON('cart/change.js', withSections({ id: d.key, quantity: d.quantity, properties: props }));
    } else {
      var target = findVariant(d, d.picks);
      variantId = target.id;
      var item = { id: Number(target.id), quantity: Number(d.quantity) || 1 };
      if (d.properties && Object.keys(d.properties).length) item.properties = d.properties;
      var before = variantQtyInCart(target.id);
      if (!item.properties && before === 0) {
        var updates = {};
        updates[d.key] = 0;
        updates[String(item.id)] = item.quantity;
        request = postJSON('cart/update.js', withSections({ updates: updates }));
      } else {
        writeJournal({ oldKey: d.key, newId: item.id, before: before, qty: item.quantity, t: Date.now() });
        request = postJSON('cart/add.js', { items: [item] }, { 'X-Kkumi-Silent': '1' })
          .then(function () {
            twoStepAdded = true;
            return postJSON('cart/change.js', withSections({ id: d.key, quantity: 0 }));
          });
      }
    }

    request
      .then(function (state) {
        clearJournal();
        paint(state, variantId);
        close(true, d.kind === 'variant' ? variantId : null);
      })
      .catch(function (err) {
        // The new variant went in but the old line wouldn't come out: keep
        // the journal and reload so recover() finishes the removal.
        if (twoStepAdded) { window.location.reload(); return; }
        clearJournal();
        if (!E) return;
        E.saving = false;
        E.err = (err && err.shopify) || copy('edError', 'We couldn’t save your changes. Your original selection is unchanged. Please try again.');
        refresh();
      });
  }

  function focusables() {
    return Array.prototype.slice.call(wrap.querySelectorAll('.kkc-dr button, .kkc-dr input:not([disabled]), .kkc-dr [tabindex="-1"]'))
      .filter(function (x) {
        if (x.offsetParent === null) return false;
        // Radios: only the checked one of a group is in the tab order.
        if (x.type === 'radio' && !x.checked && wrap.querySelector('input[name="' + x.name + '"]:checked')) return false;
        return true;
      });
  }

  function bind() {
    wrap.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-ed]') : null;
      if (!t || !E || t.tagName === 'INPUT') return;
      if (t.getAttribute('aria-disabled') === 'true') return;
      var act = t.getAttribute('data-ed');
      if (act === 'cancel') { if (!E.saving) close(false); }
      else if (act === 'save') save();
    });
    wrap.addEventListener('change', function (e) {
      var t = e.target;
      if (!E || !t || t.type !== 'radio') return;
      var n = Number(String(t.name).replace('kkc-ed-', ''));
      E.data.picks[n] = t.value;
      E.lastOption = n;
      E.err = '';
      refresh();
    });
    wrap.addEventListener('keydown', function (e) {
      if (!E) return;
      if (e.key === 'Escape') { e.preventDefault(); if (!E.saving) close(false); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('[data-kkc-edit-open]') : null;
    if (!t) return;
    e.preventDefault();
    // An issue's "Choose another option" opens the editor but focus returns
    // to the row's own Edit link.
    var line = t.closest('.kkc-line');
    var editBtn = line ? line.querySelector('.kkc-lk--edit') : null;
    open(t.getAttribute('data-kkc-edit-open'), editBtn || t);
  });
})();
